import { atom } from 'jotai'

import { nowAtom } from '@/shared/store/clock.store'

import type { Timestamps } from '~shared/utils'
import { createTimestamps, fastDeepEqual } from '~shared/utils'

import { cuesDerived } from '@/features/rundown/cue/cue.derived'

import { captureException } from '../sentry'
import * as logger from '../utils/logger'

import { rundownAtom } from './rundown.store'
import { runnerAtom } from './runner.store'

/**
 * Private value store
 */
const _timestamps = atom<Timestamps | null>(null)

/**
 * Public interface to timestamps
 * Getter: Get timestamps
 * Setter: Re-creates timestamps, takes no parameters
 */
export const timestampsAtom = atom(
  (get) => get(_timestamps),
  (get, set) => {
    // Collect params
    const cues = cuesDerived.sortedCues
    const rundown = get(rundownAtom)
    const runner = get(runnerAtom)
    const startTime = rundown?.startTime

    if (!cues.length || !rundown || !startTime) {
      set(_timestamps, null)
      return
    }

    // Create timestamps
    const now = get(nowAtom)
    let newTimestamps: Timestamps | null = null
    try {
      newTimestamps = createTimestamps(cues, rundown.cues, runner, startTime, {
        timezone: rundown.timezone ?? undefined,
        now: new Date(now),
      })
    } catch (err) {
      captureException(err)
      logger.debug('[createTimestamps] cues', cues)
      logger.debug('[createTimestamps] rundown.cues', rundown.cues)
      logger.debug('[createTimestamps] runner', runner)
      logger.debug('[createTimestamps] rundown.startTime', rundown.startTime)
    }

    // Preserve existing object signature if there are no changes
    const oldTimestamps = get(_timestamps)
    const isEqual = fastDeepEqual(newTimestamps, oldTimestamps)
    if (newTimestamps && !isEqual) set(_timestamps, newTimestamps)
  },
)

export const getTimestampByCueIdAtom = atom((get) => {
  const timestamps = get(_timestamps)

  return (cueId: string) => timestamps?.cues[cueId] ?? null
})
